import { useLocaleFormatters } from "$root/localization/hooks";
import { Text } from "@mdotm/shadcn/legacy/components";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { HistoryIcon } from "./icons/HistoryIcon";

/**
 * Sub-bar at the top of the Chat tab naming the conversation the user reopened from History.
 * It answers "which conversation am I in?" — without it, a resumed transcript and a fresh chat
 * look identical (the header only ever says "Ask Sphere AI").
 *
 * Rendered ONLY for a resumption: a chat started here stays bare, even after it lands in History
 * on its first turn. Styling follows the header's language — white surface, N100 bottom hairline,
 * the History glyph muted at N400 — so it reads as a continuation of the header, not a new surface.
 *
 * The title says WHICH conversation; `at` says WHEN it happened, which is the other half of reading
 * a record. The DAY only ("29 Jul 2026"): at this altitude that's the whole answer, and it's what
 * keeps the eye on the title. The minute a turn was written — and the zone that hour is expressed
 * in — is a different question, answered where it's actually asked: on the message itself, on hover
 * (see SphereAIThread's `messageTimeLabel`). Muted and right-aligned: it's provenance, not something
 * to act on.
 */
export function SphereAIResumedBar({ title, at }: { title: string; at?: number | null }): React.JSX.Element {
	const { formatDate } = useLocaleFormatters();
	return (
		/* The SURFACE and its hairline stay full-bleed — that rule is what continues the header's and
		   makes this read as part of it; capped at the lane it would have become a stray divider
		   floating mid-panel. The ROW inside it is what sits in the reading lane, so the title starts
		   where the transcript's text starts and the date ends where it ends. Both this bar and the
		   thread column are centred inside the same 16px gutter, which is what makes the two agree at
		   every width; at the docked width the cap is inert and only the gutter applies. */
		<div className="w-full shrink-0 border-b border-[var(--palette-N100)] bg-white px-4 py-1.5">
			<div className="mx-auto flex w-full max-w-(--thread-max-width) items-center gap-2">
				<HistoryIcon color={themeCSSVars.palette_N400} className="shrink-0" />
				{/* Body/L is the transcript's own body size (set on the thread wrapper in SphereAIThread): the
				    title names what you're reading, so it reads at the size of what you're reading — in bold,
				    since at equal size the weight is what makes it the heading of the block. */}
				<Text as="span" type="Body/L/Bold" color={themeCSSVars.palette_N700} className="min-w-0 flex-1 truncate">
					{title}
				</Text>
				{at != null && (
					<Text as="span" type="Body/S/Book" color={themeCSSVars.palette_N400} className="shrink-0">
						{formatDate(at)}
					</Text>
				)}
			</div>
		</div>
	);
}
